/**
 * Şematik göz çizimi — tek kaynak.
 *
 * NEDEN ŞEMATİK, NEDEN FOTOĞRAF DEĞİL
 *
 * Göz çevresinden ifade okuma görevinin bilinen biçimi fotoğrafla
 * kurulur. Fotoğraf bizde üç sebeple yok: kapalı bir madde kümesi
 * telifli, kendi çekimimiz için yazılı rıza gerekiyor, ve lisanslı bir
 * yüz veri tabanı sabit sayıda madde demek — "aynı tohum aynı sayfa"
 * sözü sonsuz varyant isterken.
 *
 * Çizim bu üçünü çözüyor ama bir şeyi kaybediyor: gerçek bir yüzdeki
 * mikro ifadeyi okumak ile çizilmiş bir kaşın eğimini okumak aynı
 * beceri DEĞİL. O yüzden bu görev, bilinen fotoğraflı testin adını
 * taşımıyor ve onun puanıyla karşılaştırılamaz. Ölçtüğü şey daha
 * dar ve kendi adıyla duruyor: göz çevresindeki ifadeyi okuma.
 *
 * NE TAŞIYABİLİR, NE TAŞIYAMAZ
 *
 * Çizilmiş gözler KABA durumları taşıyor: kaş çatık mı kalkık mı,
 * kapak açık mı kısık mı, bakış nereye. "Kıskandı", "utandı",
 * "mahcup oldu" gibi haller bunlardan çıkmıyor — fotoğrafta bile
 * tartışmalı. Duygu kümesi bu yüzden yedi taneyle sınırlı ve
 * `templates/sosyal.ts`'te kapalı bir tablo: geometri rastgele
 * örneklenmiyor, elle yazılmış tariflerden geliyor. Rastgele
 * örneklenseydi sayfanın doğru cevabı olmayabilirdi.
 *
 * GEOMETRİ BURADA, ŞABLONDA DEĞİL
 *
 * `render/shapes.ts` ile aynı gerekçe: yazdırma ve çocuk uygulaması
 * aynı bu dosyayı okuyor. İki kopya tutulsaydı aynı tohum iki kanalda
 * iki ayrı yüz çizerdi — bu projede tam olarak bu hata iki kez oldu
 * (örtüşen şekil kutusunun ölçüsü, `strokeDasharray`).
 *
 * RENK YOK (gorsellik.md §0.1). Her şey çizgi: kaş eğimi, kapak
 * kavisi, bakış yönü. Gri tonlamalı yazıcıda da aynı bilgi duruyor.
 */

/** Göz çizgisinin kutudaki yüksekliği */
const Y0 = 74
/** Göz yarım genişliği */
const W = 30
/** Sol ve sağ gözün merkezleri */
const CX_SOL = 60
const CX_SAG = 140
/** İris ve gözbebeği yarıçapı */
const IRIS = 12
const BEBEK = 4.5

/**
 * Bir göz çevresi çiziminin parametreleri.
 *
 * Hepsi 0–200 × 0–110 kutusundaki piksel ya da −1..1 oranı. Duygu ADI
 * BURADA YOK ve olmamalı: bu yapı çizim ağacına giriyor, çizim ağacı
 * da yazdırma HTML'ine. Adı taşısaydı sayfanın cevabı işaretlemenin
 * içinde yazardı.
 */
export interface GozCizimi {
  /** Kaşın İÇ ucunun göz çizgisinden yüksekliği */
  kasIc: number
  /** Kaşın DIŞ ucunun yüksekliği */
  kasDis: number
  /** Kaşın fazladan kavisi (kontrol noktasını yukarı iter) */
  kasKavis: number
  /** İç ucun buruna doğru kayması — çatılma */
  kasIceri: number
  /** Üst kapağın göz çizgisinin ÜSTÜNE açılma yüksekliği */
  ustAcik: number
  /**
   * Alt kapağın göz çizgisinin ALTINA inmesi.
   *
   * NEGATİF OLABİLİR ve gülen gözün tamamı budur: alt kapak yukarı
   * kavis yapıp göze giriyor, göz aşağıdan incelen bir hilale
   * dönüşüyor. İlk kurguda yalnızca küçülebiliyordu ve "mutlu" ile
   * "uykulu" ekranda birbirinin aynısı çıkıyordu — ikisi de düz kaş
   * ve dar göz. Sayfaya bakınca görüldü; imza sınaması ikisini farklı
   * sayıyordu çünkü sayılar farklıydı, çizimler değil.
   */
  altAcik: number
  /** Bakış yatay: −1 sol, +1 sağ */
  bakisX: number
  /** Bakış düşey: −1 yukarı, +1 aşağı */
  bakisY: number
  /** Dış köşenin düşmesi */
  disKose: number
  /**
   * Sağ kaş ayrı tanımlıysa ifade ASİMETRİK.
   *
   * Tek kaşı kalkık bir yüz, şematik çizimde en okunur ifadelerden
   * biri ve simetrik parametrelerle anlatılamıyor.
   */
  sagKas?: { kasIc: number; kasDis: number; kasKavis: number }
}

/**
 * Çizimin AYIRT EDİCİ imzası — sayılardan türetiliyor.
 *
 * Elle yazılmış bir etiket değil: tariften hesaplanıyor. Böylece
 * tarifteki bir sayı değişip çizim başka bir ifadeye kayarsa imza da
 * kayıyor ve sınama yakalıyor. Elle yazılsaydı tarif ile etiket sessizce
 * ayrışabilirdi.
 *
 * Üç eksen, çünkü şematik bir gözde okunabilen şey bu üçü: kaşın
 * duruşu, kapağın açıklığı, bakışın yönü.
 */
export function gozImzasi(c: GozCizimi): {
  kas: string
  acik: string
  bakis: string
  alt: string
} {
  let kas: string
  if (c.sagKas) kas = 'asimetrik'
  else if (c.kasIc - c.kasDis > 5) kas = 'ickalk'
  else if (c.kasDis - c.kasIc > 5) kas = 'cati'
  else if (c.kasIc >= 32 && c.kasDis >= 32) kas = 'kalk'
  else if (c.kasIc <= 23 && c.kasDis <= 23) kas = 'dusuk'
  else kas = 'duz'

  /*
   * Açıklık ÜST KAPAKTAN DEĞİL, gözün gerçek aralığından.
   *
   * İlk kurguda yalnızca `ustAcik`e bakıyordu. Alt kapak yukarı kavis
   * yapabildiği an (gülen göz) bu yalan söylemeye başladı: `ustAcik`
   * orta ama göz aslında ince bir hilal. İmza çizimi anlatmıyorsa
   * ayırt ediciliği denetleyen sınama da boşa çalışıyor.
   */
  const aralik = c.ustAcik + c.altAcik
  const acik = aralik >= 27 ? 'cok' : aralik <= 15 ? 'kisik' : 'orta'

  const bakis =
    Math.abs(c.bakisY) >= 0.4
      ? c.bakisY < 0
        ? 'yukari'
        : 'asagi'
      : Math.abs(c.bakisX) >= 0.4
        ? 'yan'
        : 'duz'

  /*
   * Dördüncü eksen alt kapağın YÖNÜ. Üçle yetinildiğinde "mutlu" ile
   * "uykulu" aynı sınıfa düşüyordu — oysa ekranda biri yukarı kavisli
   * bir hilal, öteki düz bir yarık.
   */
  const alt = c.altAcik < 0 ? 'kalkik' : c.altAcik <= 5 ? 'gergin' : 'gevsek'

  return { kas, acik, bakis, alt }
}

/** İmzayı karşılaştırılabilir tek dizeye indirir */
export function imzaAnahtari(c: GozCizimi): string {
  const i = gozImzasi(c)
  return `${i.kas}/${i.acik}/${i.bakis}/${i.alt}`
}

/**
 * Kaşın göz kapağına değmediği en küçük boşluk.
 *
 * Çatık kaşta boşluk kasıtlı olarak küçük — ifadenin kendisi o. Ama
 * sıfıra inerse kaş kapağın üstüne biniyor ve çizim ifade olmaktan
 * çıkıp karalamaya dönüyor. Sınama bu sayıyı denetliyor.
 */
export function kasBosluk(c: GozCizimi): number {
  const yuksek = (k: { kasIc: number; kasDis: number; kasKavis: number }) =>
    (k.kasIc + k.kasDis) / 2 + k.kasKavis / 2
  const sol = yuksek(c)
  const sag = c.sagKas ? yuksek(c.sagKas) : sol
  return Math.min(sol, sag) - c.ustAcik
}

function n(x: number): string {
  return x.toFixed(1)
}

/**
 * Bir gözün kapak yolu.
 *
 * `taraf` buruna hangi yönün baktığını söylüyor: +1 sol gözde burun
 * sağda, −1 sağ gözde solda. AYNA DÖNÜŞÜMÜ KULLANILMIYOR; kullanılsaydı
 * iris de aynalanır ve iki göz birbirine bakardı. Bakış paralel olmak
 * zorunda, yoksa her yan bakış şaşılık gibi okunuyor.
 */
function kapak(cx: number, taraf: number, c: GozCizimi): string {
  const x = (lx: number) => cx + taraf * lx
  // Dörtlü Bézier'in orta noktası (y0 + 2yc + y2)/4; tepe noktasını
  // `ustAcik` yapmak için kontrol noktası buradan çözülüyor.
  const ustKontrol = -2 * c.ustAcik - c.disKose / 2
  const altKontrol = 2 * c.altAcik - c.disKose / 2
  return (
    `M ${n(x(-W))} ${n(Y0 + c.disKose)} ` +
    `Q ${n(x(0))} ${n(Y0 + ustKontrol)} ${n(x(W))} ${n(Y0)} ` +
    `Q ${n(x(0))} ${n(Y0 + altKontrol)} ${n(x(-W))} ${n(Y0 + c.disKose)} Z`
  )
}

function kasYolu(cx: number, taraf: number, c: GozCizimi): string {
  const k = taraf < 0 && c.sagKas ? c.sagKas : c
  const x = (lx: number) => cx + taraf * lx
  const kontrol = (k.kasIc + k.kasDis) / 2 + k.kasKavis
  return (
    `M ${n(x(-(W + 4)))} ${n(Y0 - k.kasDis)} ` +
    `Q ${n(x(0))} ${n(Y0 - kontrol)} ${n(x(W + 2 - c.kasIceri))} ${n(Y0 - k.kasIc)}`
  )
}

/**
 * Bir gözün tamamı: kapak, kırpılmış iris, gözbebeği.
 *
 * İris KIRPILIYOR. Kısık bir gözde iris kapağın dışına taşıyor ve
 * taşan daire, çizimi "gözü kısık" değil "gözü patlak" gösteriyordu.
 * Kırpma, kapağın altında kalan iris kadarını bırakıyor — uykulu bir
 * gözde irisin yarısının görünmemesi ifadenin kendisi.
 */
function goz(parca: { kapak: string; kas: string; ix: number; iy: number }, kirpmaId: string): string {
  return (
    `<clipPath id="${kirpmaId}"><path d="${parca.kapak}"/></clipPath>` +
    `<path d="${parca.kapak}" fill="none" stroke="currentColor" ` +
    `stroke-width="${GOZ_OLCU.kapakCizgi}" stroke-linejoin="round"/>` +
    `<g clip-path="url(#${kirpmaId})">` +
    `<circle cx="${n(parca.ix)}" cy="${n(parca.iy)}" r="${IRIS}" fill="none" ` +
    `stroke="currentColor" stroke-width="${GOZ_OLCU.irisCizgi}"/>` +
    `<circle cx="${n(parca.ix)}" cy="${n(parca.iy)}" r="${BEBEK}" fill="currentColor"/>` +
    `</g>` +
    `<path d="${parca.kas}" fill="none" stroke="currentColor" ` +
    `stroke-width="${GOZ_OLCU.kasCizgi}" stroke-linecap="round"/>`
  )
}

/** Çizimin görüntü kutusu — iki tüketici de aynı kutuyu kullanıyor */
export const GOZ_KUTU = '0 0 200 110'

/** Çizgi kalınlıkları ve yarıçaplar: iki kanal aynı sayıları kullanıyor */
export const GOZ_OLCU = {
  kapakCizgi: 3.2,
  irisCizgi: 2.6,
  kasCizgi: 4.2,
  iris: IRIS,
  bebek: BEBEK,
}

/**
 * Bir çizimin GEOMETRİSİ: iki göz, her biri yol verisi ve iris konumu.
 *
 * `gozlerSvg` kâğıt için dize üretiyor; çocuk uygulaması aynı sayıları
 * alıp React düğümü kuruyor (`components/Sekil.tsx`'teki gerekçe: dize
 * gömmek her çizimde DOM'u baştan kurduruyor). İkisi de BU fonksiyonu
 * çağırıyor — geometri tek yerde kalıyor, ki bu dosyanın var olma
 * sebebi tam olarak o.
 */
export function gozParcalari(
  c: GozCizimi,
): { kapak: string; kas: string; ix: number; iy: number }[] {
  const orta = Y0 + (-c.ustAcik + c.altAcik) / 2 + c.disKose / 2
  return [
    { cx: CX_SOL, taraf: 1 },
    { cx: CX_SAG, taraf: -1 },
  ].map(({ cx, taraf }) => ({
    kapak: kapak(cx, taraf, c),
    kas: kasYolu(cx, taraf, c),
    /*
     * Bakış genliği: ilk kurguda 11 ve 7 idi ve EKRANDA OKUNMUYORDU.
     * İris yarıçapı 12; 3–4 pikselik bir kayma irisin kendi
     * kalınlığının altında kalıyor, göz düz bakıyor gibi duruyordu.
     * "Kuşkulu" ve "düşünüyor"un ayırt edici işareti bakış yönü
     * olduğu için bu, iki ifadeyi birden okunmaz yapıyordu. Sayfaya
     * bakınca görüldü, sınamadan çıkmadı.
     */
    ix: cx + c.bakisX * 16,
    iy: orta + c.bakisY * 11,
  }))
}

/**
 * Göz çevresi SVG'si.
 *
 * `aria-label` SABİT ve ifadeyi SÖYLEMİYOR. "kızgın gözler" yazsaydı
 * sayfanın cevabı hem işaretlemede hem ekran okuyucuda duracaktı —
 * yazdırma HTML'inde cevap anahtarının sızdığı hatanın aynısı.
 *
 * `kirpmaOneki` sayfada birden çok çizim olduğunda `clipPath`
 * kimliklerinin çakışmaması için: aynı kimlik iki kez geçtiğinde
 * tarayıcı ilkini kullanıyor ve ikinci göz birincinin kapağıyla
 * kırpılıyordu.
 */
export function gozlerSvg(c: GozCizimi, kirpmaOneki: string, boyutPx?: number): string {
  const olcu = boyutPx ? ` width="${boyutPx}"` : ''
  const parcalar = gozParcalari(c)
  return (
    `<svg viewBox="${GOZ_KUTU}"${olcu} xmlns="http://www.w3.org/2000/svg" ` +
    `role="img" aria-label="göz çizimi" focusable="false">` +
    parcalar.map((p, i) => goz(p, `${kirpmaOneki}${i}`)).join('') +
    `</svg>`
  )
}
